import { Injectable } from '@angular/core';

type PaddleEnvironment = 'sandbox' | 'live';

export interface PaddleCheckoutEvent {
  name?: string;
  type?: string;
  code?: string;
  detail?: string;
  documentation_url?: string;
  data?: {
    id?: string;
    customer?: {
      id?: string;
      email?: string;
    } | null;
    transaction_id?: string | null;
    status?: string;
    custom_data?: Record<string, unknown> | null;
    items?: Array<{
      price_id?: string;
      price?: {
        id?: string;
      } | null;
    }>;
  };
}

interface PaddleGlobal {
  Environment?: {
    set(environment: PaddleEnvironment): void;
  };
  Initialize(config: {
    token: string;
    eventCallback?: (event: PaddleCheckoutEvent) => void;
    checkout?: {
      settings?: Record<string, unknown>;
    };
  }): void;
  Update?: (config: {
    eventCallback?: (event: PaddleCheckoutEvent) => void;
  }) => void;
  Checkout: {
    open(config: Record<string, unknown>): void;
    close?: () => void;
  };
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

@Injectable({
  providedIn: 'root'
})
export class PaddleJsService {
  private scriptPromise: Promise<PaddleGlobal> | null = null;
  private initialized: { clientToken: string; environment: PaddleEnvironment } | null = null;
  private eventCallback: ((event: PaddleCheckoutEvent) => void) | null = null;

  async openInlineCheckout(config: {
    clientToken: string;
    environment: PaddleEnvironment;
    frameTarget: string;
    customerId: string;
    customerAuthToken: string | null;
    priceId: string;
    customData: Record<string, unknown>;
    onEvent: (event: PaddleCheckoutEvent) => void;
  }): Promise<void> {
    const paddle = await this.loadPaddle();
    paddle.Checkout.close?.();
    this.configureCheckout(paddle, config);

    paddle.Checkout.open({
      items: [
        {
          priceId: config.priceId,
          quantity: 1
        }
      ],
      customer: {
        id: config.customerId
      },
      customerAuthToken: config.customerAuthToken ?? undefined,
      customData: config.customData,
      settings: {
        displayMode: 'inline',
        variant: 'one-page',
        theme: this.isDarkTheme() ? 'dark' : 'light',
        locale: this.resolveLocale(),
        allowLogout: false,
        frameTarget: config.frameTarget,
        frameInitialHeight: '640',
        frameStyle: 'width: 100%; min-width: 312px; background-color: transparent; border: none;'
      }
    });
  }

  async openOverlayCheckout(config: {
    clientToken: string;
    environment: PaddleEnvironment;
    customerId: string;
    customerAuthToken: string | null;
    priceId: string;
    customData: Record<string, unknown>;
    onEvent: (event: PaddleCheckoutEvent) => void;
  }): Promise<void> {
    const paddle = await this.loadPaddle();
    paddle.Checkout.close?.();
    this.configureCheckout(paddle, config);
    paddle.Checkout.open({
      items: [{ priceId: config.priceId, quantity: 1 }],
      customer: { id: config.customerId },
      customerAuthToken: config.customerAuthToken ?? undefined,
      customData: config.customData,
      settings: {
        displayMode: 'overlay',
        variant: 'one-page',
        theme: this.isDarkTheme() ? 'dark' : 'light',
        locale: this.resolveLocale(),
        allowLogout: false
      }
    });
  }

  closeCheckout(): void {
    window.Paddle?.Checkout?.close?.();
  }

  private configureCheckout(
    paddle: PaddleGlobal,
    config: {
      clientToken: string;
      environment: PaddleEnvironment;
      onEvent: (event: PaddleCheckoutEvent) => void;
    }
  ): void {
    if (!this.initialized) {
      paddle.Environment?.set(config.environment);
      this.eventCallback = config.onEvent;
      paddle.Initialize({
        token: config.clientToken,
        eventCallback: (event) => this.eventCallback?.(event)
      });
      this.initialized = { clientToken: config.clientToken, environment: config.environment };
      return;
    }

    if (
      this.initialized.clientToken !== config.clientToken
      || this.initialized.environment !== config.environment
    ) {
      throw new Error('Paddle checkout is already initialized for a different BizzWeaver environment. Refresh the page before opening checkout.');
    }

    this.eventCallback = config.onEvent;
    paddle.Update?.({ eventCallback: (event) => this.eventCallback?.(event) });
  }

  private loadPaddle(): Promise<PaddleGlobal> {
    if (window.Paddle) {
      return Promise.resolve(window.Paddle);
    }

    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    this.scriptPromise = new Promise<PaddleGlobal>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>('script[data-paddle-js="true"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          if (window.Paddle) {
            resolve(window.Paddle);
            return;
          }
          reject(new Error('Paddle.js loaded but Paddle global is not available.'));
        });
        existingScript.addEventListener('error', () => reject(new Error('Could not load Paddle.js.')));
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      script.async = true;
      script.dataset['paddleJs'] = 'true';
      script.onload = () => {
        if (window.Paddle) {
          resolve(window.Paddle);
          return;
        }
        reject(new Error('Paddle.js loaded but Paddle global is not available.'));
      };
      script.onerror = () => reject(new Error('Could not load Paddle.js.'));
      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }

  private isDarkTheme(): boolean {
    return document.documentElement.classList.contains('ion-palette-dark');
  }

  private resolveLocale(): string {
    return navigator.language || 'en';
  }
}
